import { HqlAggregationFunction } from "../hql/index.js";
import { joinDatasetQueryPath } from "../hql/hqlNextUtils.js";
import { sentenceCase } from "../sentenceCase.js";

import { findColumnInFieldGroups } from "./exploreChartSemanticUtils.js";
import { CHART_FOLD_KEYS } from "./exploreConstants.js";
import { ExploreCustomTimeUnit } from "./exploreCustomTimeUnit.js";
import { getFieldLumpLabel } from "./exploreLumpUtils.js";
import { SemanticAwareFieldGroup } from "./exploreSemanticTypes.js";
import {
  ExploreBin,
  ExploreChartConfig,
  ExploreDetailField,
  ExploreField,
  ExploreTimeUnit,
} from "./types.js";

export function getUserOverrideableDecoratedFieldLabel(
  field: ExploreField | ExploreDetailField,
  fieldGroups: SemanticAwareFieldGroup[],
  chartConfig?: ExploreChartConfig,
): string {
  return "title" in field && field.title != null
    ? field.title
    : getDecoratedSemanticAwareFieldLabel(field, fieldGroups, chartConfig);
}

export function getDecoratedSemanticAwareFieldLabel(
  field: ExploreField | ExploreDetailField,
  fieldGroups: SemanticAwareFieldGroup[],
  chartConfig?: ExploreChartConfig,
): string {
  let label = getSemanticAwareFieldLabel(field, fieldGroups, chartConfig);

  if ("truncUnit" in field && field.truncUnit != null) {
    label = `${getExploreTimeunitLabel(field.truncUnit)} of ${label}`;
  }
  if ("aggregation" in field && field.aggregation != null) {
    label = `${getFieldAggregationLabel(field.aggregation)} of ${label}`;
  }
  if (field.queryPath != null && field.queryPath.length > 0) {
    label = `${label} (${joinDatasetQueryPath(field.queryPath)})`;
  }
  if ("lump" in field && field.lump != null) {
    label = `${label} (${getFieldLumpLabel(field.lump, true)})`;
  }
  return label;
}

export function exploreFoldKeysLabel(chartConfig: ExploreChartConfig): string {
  return chartConfig?.orientation === "horizontal"
    ? "X-Axis Field"
    : "Y-Axis Field";
}

export function getSemanticAwareFieldLabel(
  field: ExploreField | ExploreDetailField,
  fieldGroups: SemanticAwareFieldGroup[],
  chartConfig?: ExploreChartConfig,
): string {
  if (field.value === CHART_FOLD_KEYS && chartConfig != null) {
    return exploreFoldKeysLabel(chartConfig);
  }

  const semanticColumn = findColumnInFieldGroups({
    columnId: field.value,
    fieldGroups,
    queryPath: field.queryPath,
    columnAggregation: "aggregation" in field ? field.aggregation : undefined,
  });

  return semanticColumn?.columnName ?? field.value;
}

export function getExploreTimeunitLabel(timeunit: ExploreTimeUnit): string {
  return ExploreCustomTimeUnit.guard(timeunit)
    ? timeunit.name
    : (sentenceCase(timeunit) ?? "");
}

export function getFieldAggregationLabel(
  aggregation: HqlAggregationFunction,
): string {
  return aggregation === "CountDistinct"
    ? "Count distinct"
    : (sentenceCase(aggregation) ?? "");
}

export function getFieldBinLabel(
  bin: ExploreBin | undefined,
): string | undefined {
  if (bin?.useColumn === true) {
    return "Bin by value";
  }

  if (bin?.count != null) {
    return `${bin.count} bin${bin.count === 1 ? "" : "s"}`;
  }

  if (bin?.size != null) {
    return `Bin step ${bin.size}`;
  }

  return undefined;
}
